import * as React from "react";
import { Text, Group } from "@mantine/core";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";

import sampleData from "./stamps.json";

const orderedKeys = Object.keys(sampleData).sort();

interface StampDetailSidebarProps {
  stampId: string;
  onClose: () => void;
  onNavigate: (id: string) => void;
  showClose?: boolean;
}

export function StampDetailSidebar({
  stampId,
  onClose,
  onNavigate,
  showClose,
}: StampDetailSidebarProps) {
  const data = (sampleData as Record<string, any>)[stampId];
  const imgSrc = `https://vidyagiri.com/stamps/img/${stampId}.png`;

  const currentIndex = orderedKeys.indexOf(stampId);
  const prevId = currentIndex > 0 ? orderedKeys[currentIndex - 1] : null;
  const nextId =
    currentIndex < orderedKeys.length - 1
      ? orderedKeys[currentIndex + 1]
      : null;

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && prevId) onNavigate(prevId);
      if (e.key === "ArrowRight" && nextId) onNavigate(nextId);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, onNavigate, prevId, nextId]);

  if (!data) return null;

  const smallCaps: React.CSSProperties = {
    fontFamily: "IM Fell English, serif",
    fontSize: "10px",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#9b9974",
    display: "block",
    marginBottom: "4px",
  };

  const navBtnStyle = (enabled: boolean): React.CSSProperties => ({
    background: "none",
    border: "none",
    cursor: enabled ? "pointer" : "default",
    opacity: enabled ? 1 : 0.2,
    color: "#9b9974",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "16px",
    fontFamily: "IM Fell English, serif",
    padding: 0,
  });

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "transparent",
        overflow: "hidden",
      }}
    >
      {/* Close side panel — centered above prev/next */}
      {showClose && (
        <div style={{ display: "flex", justifyContent: "center", padding: "0.75rem 2rem 0", flexShrink: 0 }}>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#9b9974",
              fontFamily: "IM Fell English, serif",
              fontSize: "10px",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              padding: "4px 8px",
            }}
          >
            close detail
          </button>
        </div>
      )}

      {/* Prev / Next nav — top */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: showClose ? "0.5rem 2rem 0.75rem" : "1.25rem 2rem 0.75rem",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={smallCaps}>previous</span>
          <button onClick={() => prevId && onNavigate(prevId)} disabled={!prevId} aria-label="Previous stamp" style={navBtnStyle(!!prevId)}>
            <IconChevronLeft size={16} />
            {prevId ? <span>{prevId}</span> : <span style={{ fontSize: "13px" }}>—</span>}
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
          <span style={{ ...smallCaps, textAlign: "right" }}>next</span>
          <button onClick={() => nextId && onNavigate(nextId)} disabled={!nextId} aria-label="Next stamp" style={{ ...navBtnStyle(!!nextId), flexDirection: "row-reverse" }}>
            <IconChevronRight size={16} />
            {nextId ? <span>{nextId}</span> : <span style={{ fontSize: "13px" }}>—</span>}
          </button>
        </div>
      </div>

      {/* Stamp image — fills remaining space */}
      <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 2rem" }}>
        <img
          src={imgSrc}
          alt={`stamp ${stampId}`}
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }}
        />
      </div>

      {/* Bottom info — fixed, hugging bottom */}
      <div
        style={{
          flexShrink: 0,
          padding: "1.75rem 2rem 1.75rem",
          overflowY: "auto",
          maxHeight: "40%",
        }}
      >
        <Text size={16} style={{ color: "#9b9974", fontFamily: "IM Fell English, serif" }}>
          [{stampId}]
        </Text>
        <Text
          size={48}
          transform="lowercase"
          style={{ fontFamily: "IM Fell English, serif", lineHeight: 1.1, marginTop: "0.4rem" }}
        >
          {data.country}
        </Text>
        <Text size="sm" style={{ lineHeight: 1.7, color: "#9b9974", marginTop: "1rem" }}>
          {data.description}
        </Text>
        {(data.link1 || data.link2 || data.link3) && (
          <Group mt={20} spacing={10}>
            <Text size="sm" italic style={{ color: "#9b9974" }}>
              {[data.link1, data.link2, data.link3].filter(Boolean).length === 1 ? "reference" : "references"}
            </Text>
            {data.link1 && <Text size="sm"><a href={data.link1} target="_blank" rel="noopener noreferrer">i</a></Text>}
            {data.link2 && <Text size="sm"><a href={data.link2} target="_blank" rel="noopener noreferrer">ii</a></Text>}
            {data.link3 && <Text size="sm"><a href={data.link3} target="_blank" rel="noopener noreferrer">iii</a></Text>}
          </Group>
        )}
      </div>
    </div>
  );
}
